/**
 * Сборка отчёта по накопленному навыку (ТЗ FR-EN-28 … FR-EN-31).
 *
 * Четыре раздела — клавиши, сочетания, слова и переключение раскладок — и не
 * больше двадцати строк в каждом (FR-EN-28, FR-EN-29). Единица без единой
 * попытки в отчёт не попадает, а пустой раздел показывается пустым, а не
 * скрывается: отсутствие данных — тоже ответ (FR-EN-30). Отчёт ничего не
 * меняет и никуда не ходит (FR-EN-31).
 *
 * Порядок строк задаёт та же функция приоритета, что отбирает материал в
 * тренировку. Иначе отчёт и занятие разошлись бы: пользователь увидел бы
 * вверху одно, а получил в упражнении другое — и перестал бы верить обоим.
 *
 * Отчёт ничего не меняет: он читает состояние навыка и возвращает его в
 * виде, пригодном для показа.
 */

import type { MasteryStat, MasteryUnit } from '@type2x/shared-contracts';
import { MASTERED_THRESHOLD } from '@type2x/shared-contracts';
import { computePriority, rankUnits } from '@type2x/learning-engine';

import type {
  ReportInput,
  ReportRow,
  ReportSection,
  ReportSectionId,
  SkillReport,
} from '../contracts/report.js';
import type { SectionQuery, SectionView } from '../contracts/report.js';
import { REPORT_SECTIONS, ROWS_PER_PAGE } from '../contracts/report.js';

/**
 * Приставка идентификаторов переходов между раскладками.
 *
 * По виду единицы их не отличить: переход — такой же `pattern`, как
 * оператор из пакета кода. Различает их только идентификатор.
 */
const SWITCH_PREFIX = 'switch:';

/**
 * В какой раздел отчёта попадает единица.
 *
 * Знак препинания — это клавиша, и место ему среди клавиш: пользователь
 * ищет его там же, где запятую на клавиатуре, а не среди сочетаний.
 */
export function sectionOf(unit: MasteryUnit): ReportSectionId {
  if (unit.id.startsWith(SWITCH_PREFIX)) return 'switches';

  switch (unit.kind) {
    case 'key':
    case 'punctuation':
      return 'keys';
    case 'word':
      return 'words';
    case 'bigram':
    case 'trigram':
    case 'pattern':
      return 'combinations';
  }
}

/** Строка отчёта по единице и её состоянию. */
function toRow(unit: MasteryUnit, stat: MasteryStat, priority: number): ReportRow {
  return {
    unitId: unit.id,
    content: unit.content,
    attempts: stat.attempts,
    errorRate: stat.errorRate,
    medianLatencyMs: stat.medianLatencyMs,
    masteryScore: stat.masteryScore,
    nextReviewAt: stat.nextReviewAt,
    priority,
    mastered: stat.masteryScore >= MASTERED_THRESHOLD,
  };
}

/**
 * Единицы, о которых есть что сказать.
 *
 * Единица без попыток в отчёт не идёт: её создал какой-то режим, но
 * пользователь её ещё не набирал, и «ноль ошибок из нуля» — не сведения.
 */
function touched(input: ReportInput): MasteryUnit[] {
  return Object.values(input.units).filter((unit) => {
    const stat: MasteryStat | undefined = input.stats[unit.id];
    return stat !== undefined && stat.attempts > 0;
  });
}

/**
 * Строит отчёт по текущему состоянию навыка.
 *
 * @param input единицы, их состояние, конфигурация и контекст приоритета.
 */
export function buildSkillReport(input: ReportInput): SkillReport {
  const units = touched(input);

  const ranked = rankUnits(
    units.map((unit) => computePriority(unit, input.stats[unit.id]!, input.config, input.context)),
  );

  const bySection = new Map<ReportSectionId, ReportRow[]>(REPORT_SECTIONS.map((id) => [id, []]));

  for (const result of ranked) {
    bySection
      .get(sectionOf(result.unit))!
      .push(toRow(result.unit, input.stats[result.unit.id]!, result.priority));
  }

  const sections: ReportSection[] = REPORT_SECTIONS.map((id) => ({
    id,
    rows: bySection.get(id)!,
  }));

  return {
    sections,
    untouched: Object.keys(input.units).length - units.length,
    builtAt: input.now,
  };
}

/** Подходит ли строка под фильтр. */
function matchesFilter(row: ReportRow, query: SectionQuery): boolean {
  switch (query.filter) {
    case 'all':
      return true;
    case 'weak':
      // Слабое — не освоенное. Порог тот же, что и у режима «Мои ошибки»:
      // отчёт и тренировка не должны расходиться в том, что считать слабым.
      return !row.mastered;
    case 'due':
      return row.nextReviewAt <= query.now;
    case 'mastered':
      return row.mastered;
  }
}

/** Подходит ли строка под поисковый запрос. */
function matchesQuery(row: ReportRow, needle: string): boolean {
  if (needle === '') return true;
  return row.content.toLocaleLowerCase().includes(needle);
}

/**
 * Что показать в разделе по запросу пользователя.
 *
 * Порядок строк остаётся прежним — по убыванию приоритета: поиск и фильтр
 * отбирают, но не переставляют. Иначе одна и та же единица оказывалась бы то
 * выше, то ниже в зависимости от того, что рядом с ней ищут.
 */
export function viewSection(section: ReportSection, query: SectionQuery): SectionView {
  const needle = query.query.trim().toLocaleLowerCase();
  const matched = section.rows.filter(
    (row) => matchesFilter(row, query) && matchesQuery(row, needle),
  );

  // Страница есть всегда, даже когда показывать нечего: «страница 1 из 0» —
  // не сведения, а недоразумение.
  const pages = Math.max(1, Math.ceil(matched.length / ROWS_PER_PAGE));
  const page = Math.min(Math.max(query.page, 1), pages);
  const start = (page - 1) * ROWS_PER_PAGE;

  return {
    rows: matched.slice(start, start + ROWS_PER_PAGE),
    matched: matched.length,
    total: section.rows.length,
    page,
    pages,
  };
}
